import styled, { useTheme } from 'styled-components';

import type { IButtonProps } from '@klv/atoms/button/types';
import Buttons from '@klv/molecules/buttons';
import Typography from '@klv/atoms/typography';
import Icon from '@klv/atoms/icon';
import type { IIcon } from '@klv/atoms/icon/types';
import type { TColorVariant } from '@klv/shared/styles/types';
import Grid from '@klv/utils/grid';

interface IListBullet {
  text: string;
}

export interface IBulletedList {
  background: TColorVariant;
  bullets?: IListBullet[];
  buttons?: IButtonProps[];
  headline?: string;
}

const StyledBulletedList = styled.div<{ $background: TColorVariant; }>`
  ${({ $background, theme }) => `
    ${theme.globalPadding};
    background-color: ${theme.bulletedList[$background].background};
    color: ${theme.bulletedList[$background].text};
  `}
`;

const StyledHeadline = styled(Typography)`
  grid-column: span 8;
  margin-bottom: 3.2rem;
  text-align: center;

  ${({ theme }) => theme.screenSize.s576.mediaQuery} {
    grid-column: span 12;
  }

  ${({ theme }) => theme.screenSize.s768.mediaQuery} {
    margin-bottom: 5.6rem;
  }
`;

const StyledBullets = styled.ul`
  display: contents;
  list-style: none;
`;

const StyledBullet = styled.li`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  grid-column: 2 / span 6;
  margin-bottom: 2.4rem;

  ${({ theme }) => `
    ${theme.screenSize.s576.mediaQuery} {
      grid-column: 5 / span 6;
    }
    ${theme.screenSize.s768.mediaQuery} {
      grid-column: 2 / span 3;
      margin-bottom: 1.6rem;

      &:nth-child(3n-1) {
        grid-column: 6 / span 3;
      }

      &:nth-child(3n) {
        grid-column: 10 / span 3;
      }
    }
  `}
`;

const BulletIcon = styled(Icon)<IIcon>`
  padding: 0.2rem;
  margin-right: 1.2rem;
  flex-shrink: 0;
`;

const BulletText = styled(Typography)`
  font-weight: 500;
  line-height: 1.5;
  font-size: 1.6rem;
`;

const StyledButtons = styled(Buttons)`
  grid-column: span 8;
  justify-self: center;
  margin-top: 0.8rem;

  ${({ theme }) => `
    ${theme.screenSize.s576.mediaQuery} {
      grid-column: span 12;
    }
    ${theme.screenSize.s768.mediaQuery} {
      margin-top: 3.2rem;
    }
  `}
`;

const BulletedList = ({
  background, bullets, buttons, headline = '', ...props
}: IBulletedList) => {
  const theme = useTheme();
  const buttonColor = theme.bulletedList[background].button;

  return (
    <StyledBulletedList $background={background} {...props}>
      <Grid>
        {headline && <StyledHeadline colorVariant={background === 'dark' ? 'light' : 'dark'} forwardedAs="h2" variant="h4">{headline}</StyledHeadline> }
        {bullets && bullets?.length > 0 && (
        <StyledBullets>
          {bullets.map((bullet) => (
            <StyledBullet key={bullet.text}>
              <BulletIcon fillColor="currentColor" iconName="CheckBullet" size={2.4} aria-hidden="true" />
              <BulletText forwardedAs="span" variant="tagline" key={bullet.text}>{bullet.text}</BulletText>
            </StyledBullet>
          ))}
        </StyledBullets>
        )}
        {buttons && buttons?.length > 0 && (
          <StyledButtons
            buttons={buttons}
            buttonColor={buttonColor}
            variants={['tertiary']}
          />
        )}
      </Grid>
    </StyledBulletedList>
  );
};

export default BulletedList;
